<template>
    <v-container class="planning-saved px-6 pt-0 pb-3">
    <v-dialog
        v-model="create_plan_dialog"
        width="2000"
        content-class="planning-create-dialog"
    >
        <ForecastingCreate
            v-if="create_plan_dialog"
            :project_id="project_id"
            :data_version="data_version"
            :data_levels="data_levels"
            :project_date_range="project_date_range"
            :project_filters="project_filters"
            :parent_id="create_plan_parent_id"
            v-on:close="create_plan_dialog = false"
            v-on:plan_created="handle_created_plan"
            v-on:update_campaign_plan_id="update_created_plan"
            v-on:date_update="forward_date_update"
            v-on:triggerAlert="forward_alert"
        />
    </v-dialog>
    <v-row class="planning-saved__content ma-0 pa-0">
        <v-col cols="12" v-if="page_loading && forecasting_reports.length === 0">
            <v-row class="planning-saved__loading ma-0 pa-0" align="center" justify="center">
                <v-col cols="auto">
                    <v-progress-circular color="black" size="100" indeterminate></v-progress-circular>
                </v-col>
            </v-row>
        </v-col>
        <v-col cols="12" v-else class="planning-saved__directory ma-0 pa-0">
            <EntityDirectory
                entity_name="Plan"
                :directory_data="forecasting_report_directory_data"
                :loading="directory_loading"
                :available_create_options="['create_folder', 'create_file']"
                :item_subcontent_config="item_subcontent_config"
                :auto_select_first_folder="true"
                v-on:open_entity="open_plan"
                v-on:edit_entity_name="update_report"
                v-on:delete_entity="delete_plan"
                v-on:move_files="move_plans"
                v-on:create_entity_folder="create_plan_folder"
                v-on:create_entity="create_plan"
                v-on:reset_directory="get_forecasting_reports"
            />
        </v-col>
    </v-row>
    </v-container>
</template>
  
<script>
  import EntityDirectory from "@/components/Utility/EntityDirectory.vue";
  import ForecastingCreate from "./ForecastingCreate.vue";

  import planning_api_caller from "@/javascript/forecasting.js";
  import { ALERT_TYPE_ERROR, ALERT_TYPE_SUCCESS, ALERT_GENERIC_MESSAGE, CAMPAIGN_PLAN_NAME_UPDATED } from "@/constants/constants";

  export default {
    name: "ForecastingSaved",
    props: ["project_id", "data_version", "data_levels", "project_date_range", "reset", "project_filters"],
    components: {
        EntityDirectory,
        ForecastingCreate
    },
    data: () => ({
        page_loading: true,
        create_plan_dialog: false,
        create_plan_parent_id: -1,

        plans_loading: true,

        forecasting_reports: [],
        available_forecasting_report_lookup: {},
        item_subcontent_config: [
            { key: "updated_time", label: "Last Update" },
            { key: "created_time", label: "Created" },
        ],

    }),
    async mounted(){
        await this.get_forecasting_reports();
        this.page_loading = false;
    },
    computed: {
        forecasting_report_directory_data() {
            return this.forecasting_reports.map((item) =>
                this.normalizeForecastingReportDirectoryItem(item)
            );
        },
        directory_loading() {
            return this.plans_loading;
        },
    },
    methods: {
        normalizeForecastingReportDirectoryItem(item) {
            if (
                item.is_directory === true ||
                (item.is_directory !== false && Array.isArray(item.children))
            ) {
                return {
                    ...item,
                    id: item.id || item.plan_id || item.report_id,
                    name: item.name || item.plan_name || item.folder_name || "Untitled Folder",
                    is_directory: true,
                    children: (item.children || []).map((child) =>
                        this.normalizeForecastingReportDirectoryItem(child)
                    ),
                };
            }

            return this.normalizeForecastingReportFile(item);
        },
        normalizeForecastingReportFile(item) {
            const reportId = item.id || item.plan_id || item.report_id;
            const availableReport = this.available_forecasting_report_lookup[String(reportId)] || {};
            const planStatus = item.plan_status || item.status;
            const planStage = item.stage || item.plan_stage || item.report_stage;
            const planName = item.name || item.plan_name || "Untitled Plan";
            const updatedTime =
                item.updated_time ||
                item.updated_at ||
                item.last_updated ||
                item.created_time;

            return {
                ...availableReport,
                ...item,
                id: reportId,
                report_id: reportId,
                data_filters: availableReport.data_filters || item.data_filters,
                data_levels: availableReport.data_levels || item.data_levels,
                name: planName,
                is_directory: false,
                children: item.children || [],
                status: "ACTIVE",
                plan_status: planStatus,
                stage: planStage,
                display_status: planStatus,
                created_time: item.created_time,
                updated_time: updatedTime,
            };
        },
        parseReportDataLevels(value) {
            const validLevels = ["channel", "format", "media_type"];
            const normalizeLevels = (levels) => {
                return levels
                    .flatMap((level) => String(level).split(/\s*(?:,|\||\+| - )\s*/))
                    .map((level) => level.trim())
                    .map((level) => level.toLowerCase())
                    .filter((level) => validLevels.includes(level));
            };

            if (Array.isArray(value)) {
                return normalizeLevels(value);
            }
            if (value && Array.isArray(value.selected_levels)) {
                return normalizeLevels(value.selected_levels);
            }
            if (value && Array.isArray(value.data_levels)) {
                return normalizeLevels(value.data_levels);
            }
            if (typeof value !== "string") {
                return [];
            }

            return normalizeLevels(value
                .split(/[|,]/)
                .map((level) => level.trim())
                .filter((level) => level !== ""));
        },
        parseReportDataFilters(value) {
            const validLevels = ["channel", "format", "media_type"];
            if (value && typeof value === "object" && !Array.isArray(value)) {
                let filters = {};
                for (const level of validLevels) {
                    if (Array.isArray(value[level])) {
                        filters[level] = value[level];
                    }
                }
                return Object.keys(filters).length > 0 ? filters : null;
            }
            if (typeof value !== "string" || value.trim() === "") {
                return null;
            }

            let filters = {};
            for (const filterGroup of value.split("[]")) {
                const [level, rawValues] = filterGroup.split("~");
                if (!level || rawValues === undefined) {
                    continue;
                }

                const normalizedLevel = level.trim();
                if (!validLevels.includes(normalizedLevel)) {
                    continue;
                }

                filters[normalizedLevel] = rawValues
                    .split("||")
                    .map((filterValue) => filterValue.trim())
                    .filter((filterValue) => filterValue !== "");
            }

            return Object.keys(filters).length > 0 ? filters : null;
        },
        buildAvailableForecastingReportLookup(reports) {
            let lookup = {};
            for (const report of reports || []) {
                const reportId = report.report_id || report.id || report.plan_id;
                if (reportId === undefined || reportId === null) {
                    continue;
                }

                lookup[String(reportId)] = {
                    ...report,
                    data_levels: this.parseReportDataLevels(report.data_levels || report.ad_unit),
                    data_filters: this.parseReportDataFilters(report.data_filters),
                };
            }

            return lookup;
        },
        findPlanById(items, planId) {
            if (!Array.isArray(items)) {
                return null;
            }

            for (const item of items) {
                if (String(item.id) === String(planId) && !item.is_directory) {
                    return item;
                }

                const nestedMatch = this.findPlanById(item.children, planId);
                if (nestedMatch) {
                    return nestedMatch;
                }
            }

            return null;
        },
        findDirectoryItemById(items, itemId) {
            if (!Array.isArray(items)) {
                return null;
            }

            for (const item of items) {
                if (item.id === itemId) {
                    return item;
                }

                const nestedMatch = this.findDirectoryItemById(item.children, itemId);
                if (nestedMatch) {
                    return nestedMatch;
                }
            }

            return null;
        },
        open_create_plan_dialog(parentId) {
            this.create_plan_parent_id = parentId;
            this.create_plan_dialog = true;
        },
        update_created_plan(planId, name, reportMetadata) {
            this.$emit("create_campaign_plan_id", planId, name, reportMetadata);
        },
        async handle_created_plan() {
            await this.get_forecasting_reports();
        },
        forward_date_update(payload) {
            this.$emit("date_update", payload);
        },
        forward_alert(type, message) {
            this.$emit("triggerAlert", type, message);
        },
        getDefaultSelectedLevels() {
            if (
                this.data_levels &&
                Array.isArray(this.data_levels.selected_levels) &&
                this.data_levels.selected_levels.length > 0
            ) {
                return this.data_levels.selected_levels;
            }

            if (
                this.data_levels &&
                Array.isArray(this.data_levels.data_levels) &&
                this.data_levels.data_levels.length > 0
            ) {
                return [this.data_levels.data_levels[0]];
            }

            return [];
        },
        async fetchPlanDateRange(planId) {
            const dateRange = {
                start_date: this.project_date_range ? this.project_date_range.start_date : null,
                end_date: this.project_date_range ? this.project_date_range.end_date : null,
            };
            const payload = {
                project_id: this.project_id,
                plan_id: planId,
                date_range: dateRange,
                selected_level: this.getDefaultSelectedLevels(),
            };

            let token = await this.$auth.getTokenSilently();
            let result = await planning_api_caller
                .get_campaign_plan_data(token, payload)
                .then((resp) => {
                    if (resp["success"]) {
                        return resp["data"];
                    }
                    return false;
                })
                .catch(() => {
                    return false;
                });

            if (
                result &&
                result.campaign_detail &&
                result.campaign_detail.start_date &&
                result.campaign_detail.end_date
            ) {
                return {
                    start_date: result.campaign_detail.start_date,
                    end_date: result.campaign_detail.end_date,
                };
            }

            return {
                start_date: null,
                end_date: null,
            };
        },
        async fetchPlanDetail(planId) {
            const dateRange = {
                start_date: this.project_date_range ? this.project_date_range.start_date : null,
                end_date: this.project_date_range ? this.project_date_range.end_date : null,
            };
            const payload = {
                project_id: this.project_id,
                plan_id: planId,
                date_range: dateRange,
                selected_level: this.getDefaultSelectedLevels(),
            };

            let token = await this.$auth.getTokenSilently();
            return planning_api_caller
                .get_campaign_plan_data(token, payload)
                .then((resp) => {
                    if (resp["success"]) {
                        return resp["data"];
                    }
                    return false;
                })
                .catch(() => {
                    return false;
                });
        },
        getReportMetadataFromItem(item) {
            if (!item) {
                return null;
            }

            const metadataSources = [
                item,
                item.campaign_detail,
                item.forecasting_report_detail,
                item.report_payload,
                item.payload,
                item.campaign_detail ? item.campaign_detail.report_payload : null,
                item.campaign_detail ? item.campaign_detail.payload : null,
                item.forecasting_report_detail ? item.forecasting_report_detail.report_payload : null,
                item.forecasting_report_detail ? item.forecasting_report_detail.payload : null,
            ].filter((source) => {
                return source && typeof source === "object";
            });

            for (const source of metadataSources) {
                const dataFilters = this.parseReportDataFilters(source.data_filters);
                if (!dataFilters) {
                    continue;
                }

                return {
                    data_levels: this.parseReportDataLevels(source.data_levels || source.ad_unit),
                    data_filters: dataFilters,
                };
            }

            return null;
        },
        async resolvePlanMetadata(plan, planId) {
            const listMetadata = this.getReportMetadataFromItem(plan);
            if (listMetadata) {
                return listMetadata;
            }

            const planDetail = await this.fetchPlanDetail(planId);
            return this.getReportMetadataFromItem(planDetail);
        },
        async resolvePlanDateRange(plan, planId) {
            const planStartDate = plan ? plan.start_date || plan.report_start_date : null;
            const planEndDate = plan ? plan.end_date || plan.report_end_date : null;

            if (planStartDate && planEndDate) {
                return {
                    start_date: planStartDate,
                    end_date: planEndDate,
                };
            }

            return this.fetchPlanDateRange(planId);
        },
        get_forecasting_reports: async function () {
            this.plans_loading = true;
            let payload = {
                project_id: this.project_id,
            };

            let token = await this.$auth.getTokenSilently();
            let result = await planning_api_caller
                .get_campaign_plan(token, payload)
                .then((resp) => {
                if (resp["success"]) {
                    return resp["data"];
                }
                return false;
                })
                .catch(() => {
                return false;
            });
            this.available_forecasting_report_lookup = result && result["available_forecasting_reports"]
                ? this.buildAvailableForecastingReportLookup(result["available_forecasting_reports"])
                : {};
            const forecastingReports = result
                ? result["forecasting_reports"] || result["available_campaigns"]
                : null;

            if (forecastingReports) {
                this.forecasting_reports = forecastingReports;
            } else {
                this.$emit("triggerAlert", ALERT_TYPE_ERROR, ALERT_GENERIC_MESSAGE);
            }
            this.plans_loading = false;
        },
        create_plan_folder: async function(payload) {
            this.plans_loading = true;

            const createPayload = {
                project_id: this.$project.get_val(),
                parent_id: payload.parent_id,
                folder_name: payload.folder_name,
            };

            let token = await this.$auth.getTokenSilently();
            let result = await planning_api_caller
                .create_campaign_plan_folder(token, createPayload)
                .then((resp) => {
                    if (resp["success"]) {
                        return resp["data"];
                    }
                    return false;
                })
                .catch(() => {
                    return false;
                });

            if (result) {
                await this.get_forecasting_reports();
            } else {
                this.$emit("triggerAlert", ALERT_TYPE_ERROR, ALERT_GENERIC_MESSAGE);
                this.plans_loading = false;
            }
        },
        create_plan: function(parentId) {
            this.open_create_plan_dialog(parentId);
        },
        delete_plan_folder: async function(folder_id) {
            this.plans_loading = true;

            const payload = {
                project_id: this.project_id,
                folder_id,
            };

            let token = await this.$auth.getTokenSilently();
            let result = await planning_api_caller
                .delete_campaign_plan(token, payload)
                .then((resp) => {
                    if (resp["success"]) {
                        return resp["data"];
                    }
                    return false;
                })
                .catch(() => {
                    return false;
                });

            if (!result) {
                this.$emit("triggerAlert", ALERT_TYPE_ERROR, ALERT_GENERIC_MESSAGE);
            }

            await this.get_forecasting_reports();
        },
        delete_plan: async function(plan_id){
            const selectedItem = this.findDirectoryItemById(this.forecasting_report_directory_data, plan_id);

            if (selectedItem && selectedItem.is_directory) {
                await this.delete_plan_folder(plan_id);
                return;
            }

            this.page_loading = true;
            let payload = {
                report_id: plan_id,
                project_id: this.project_id,
            };

            let token = await this.$auth.getTokenSilently();
            let result = await planning_api_caller
            .delete_campaign_plan(token, payload)
            .then((resp) => {
                if (resp["success"]) {
                return resp["data"];
                }
                return false;
            })
            .catch(() => {
                return false;
            });

            if (!result || !result["report_id"]) {
                this.$emit("triggerAlert", ALERT_TYPE_ERROR, ALERT_GENERIC_MESSAGE);
            }
            await this.get_forecasting_reports();
            this.plans_loading = false;
            this.page_loading = false;
        },
        move_plans: async function(payload) {
            const reportIds = (payload.entity_ids || []).map((id) => Number(id));

            if (reportIds.length === 0 || payload.parent_id === null || payload.parent_id === undefined) {
                this.$emit("triggerAlert", ALERT_TYPE_ERROR, ALERT_GENERIC_MESSAGE);
                return;
            }

            this.plans_loading = true;
            const movePayload = {
                project_id: this.project_id,
                report_ids: reportIds,
                parent_id: payload.parent_id,
            };

            let token = await this.$auth.getTokenSilently();
            let result = await planning_api_caller
                .move_reports(token, movePayload)
                .then((resp) => {
                    if (resp["success"]) {
                        return resp["data"];
                    }
                    return false;
                })
                .catch(() => {
                    return false;
                });

            if (result) {
                await this.get_forecasting_reports();
            } else {
                this.$emit("triggerAlert", ALERT_TYPE_ERROR, ALERT_GENERIC_MESSAGE);
                this.plans_loading = false;
            }
        },
        open_plan: async function (plan_id) {
            if (this.directory_loading) {
                return;
            }

            const plan = this.findPlanById(this.forecasting_report_directory_data, plan_id);
            const planName = plan ? plan.name : null;
            const planStage = plan ? plan.stage : null;
            const planDateRange = await this.resolvePlanDateRange(plan, plan_id);
            const planMetadata = await this.resolvePlanMetadata(plan, plan_id);

            this.$emit("date_update", {
                start_date: planDateRange.start_date,
                end_date: planDateRange.end_date,
            });

            this.$emit("update_campaign_plan_id", plan_id, planName, planStage, planMetadata);
        },
        update_plan_name: async function (payload) {
            const item = this.findDirectoryItemById(this.forecasting_report_directory_data, payload.id);

            if (item && item.is_directory) {
                await this.update_plan_folder(payload);
                return;
            }

            const plan = this.findPlanById(this.forecasting_report_directory_data, payload.id);
            const updatePayload = {
                project_id: this.project_id,
                plan_id: payload.id,
                status: plan ? plan.plan_status : null,
                plan_name: payload.name
            };

            await this.save_forecasting_report(updatePayload);
        },
        update_report: async function(payload) {

            let plan_id = payload.id;
            let plan_name = payload.name;

            if (plan_id) {
                let update_payload = {
                    client_id: this.$project.get_val().slice(0, 4),
                    report_id: plan_id,
                    update_vals: {
                        report_name: plan_name,
                    },
                };

                this.plans_loading = true;

                let token = await this.$auth.getTokenSilently();
                let result = await planning_api_caller
                    .update_campaign_plan(token, update_payload)
                    .then((resp) => {
                        if (resp["success"]) {
                            return resp["data"];
                        }
                        return false;
                    })
                    .catch(() => {
                        return false;
                    });

                if (!result) {
                    this.$emit("triggerAlert", ALERT_TYPE_ERROR, ALERT_GENERIC_MESSAGE);
                } else {
                    this.$emit("triggerAlert", ALERT_TYPE_SUCCESS, "Folder name updated successfully");
                }

                await this.get_forecasting_reports();
            }
        },
        save_forecasting_report: async function(updatePayload) {
            if (updatePayload["plan_id"]) {

                let token = await this.$auth.getTokenSilently();
                let result = await planning_api_caller
                    .update_campaign_plan(token, updatePayload)
                    .then((resp) => {
                        if (resp["success"]) {
                        return resp["data"];
                        }
                        return false;
                    })
                    .catch(() => {
                        return false;
                    });

                if (!result || !result["campaign_plan_id"]) {
                    this.$emit("triggerAlert", ALERT_TYPE_ERROR, ALERT_GENERIC_MESSAGE);
                } else {
                    this.$emit("triggerAlert", ALERT_TYPE_SUCCESS, CAMPAIGN_PLAN_NAME_UPDATED);
                }
            }
            await this.get_forecasting_reports();
        },
    }
  };
  </script>
  
  <style>
    .planning-saved {
        max-width: 100%;
        height: 100%;
        font-family: "Poppins";
    }

    .planning-saved__content {
        width: 100%;
        height: 100%;
        align-content: flex-start;
    }

    .planning-saved__loading,
    .planning-saved__directory {
        width: 100%;
        height: 100%;
        min-height: 0;
    }

    .planning-create-dialog {
        max-height: calc(100vh - 48px) !important;
        overflow-y: hidden !important;
    }
  </style>
  
